import {
  ENGAGEMENT_PHASES,
  LEVEL_LABELS,
  LEVEL_SCOPES,
  MODULE_LEVELS,
  MODULE_REGISTRY,
  REMOVED_MODULES,
  REV_A,
  REV_A_RECONCILIATION,
  engagementPhaseFor,
  modulesAtLevel,
  type EngagementPhase,
  type ModuleEntry,
  type ModuleLevel,
} from '@/modules/architecture'
import { FLOW_SCOPE_TAIL, FLOW_STEPS, flowStepByN } from '@/demo/customerFlow'
import { PHASES, type PhaseKey } from '@/demo/data'

/** Content for `/architecture` — the Rev A map plus the customer flow. */

export const ARCHITECTURE_CHROME = {
  moduleLabel: 'ADMINISTRATION',
  breadcrumb: 'Architecture map',
} as const

export const ARCHITECTURE_HEADER = {
  title: 'Architecture map',
  ledeBefore: 'What this platform is made of, how a client gets onto it, and which of it the prototype actually realises. Source: ',
  source: `px-module-flow Rev ${REV_A.rev}`,
  ledeMiddle: ` (${REV_A.dated}, ${REV_A.sheets} sheets) and the `,
  flowSource: 'environment-to-migration customer flow',
  ledeAfter: '.',
  contextPill: `REV ${REV_A.rev} · ${REV_A.dated}`,
} as const

export const SECTION_HEADINGS = {
  loop: 'The three modules, and the loop',
  phases: 'Seven phases, seven gates',
  flow: 'The customer flow — environment to migration',
  crosswalk: 'Two lifecycles, one crosswalk',
  inventory: `Module inventory · ${MODULE_LEVELS[0]} – ${MODULE_LEVELS[4]}`,
  runAndEvolve: 'Run & evolve · sheet 3 — OUT OF SCOPE',
} as const

export const LOOP_LABELS = {
  evidence: 'Evidence flows back',
} as const

export const PHASES_TABLE = {
  columns: ['#', 'Phase', 'Gate', 'Exit criteria', 'Agent leverage', 'Realised by'],
  preProject: 'pre-project — no delivery screen',
  leverageTitle: (n: number, max: number) => `Agent leverage ${n} of ${max}`,
} as const

export const FLOW_INTRO =
  'How a client actually arrives on the platform and gets to work: ten steps, one approval gate, and the modules each step wakes up. Steps 01–06 onboard; 07–10 are the delivery phases the engagement lifecycle above already names.'

/**
 * The FIXED CHROME labels of the customer-flow section. Every other string it
 * renders comes from `@/demo/customerFlow`, which is a verbatim transcription;
 * these are the table furniture the source does not supply.
 */
export const FLOW_CHROME = {
  tableCaption: 'The ten-step customer flow with its gate and module activation',
  columns: ['Step', 'What happens · what it creates', 'Gate', 'Modules that fire', 'Detail'],
  onboardingGroup: 'Onboarding',
  deliveryGroup: 'Delivery',
  stepPrefix: 'STEP ',
  cannotProceed: 'Cannot proceed until',
  gatePill: 'GATE',
  gateOutcomes:
    'Approve leaves the project Confirmed with the configuration version-locked; return leaves it Draft with a change request raised.',
  edgesLabel: 'edges',
  edgeWord: 'edge',
  toWord: 'to',
  live: 'live',
  dormant: 'dormant',
  actionsWord: 'actions',
  behavioursWord: 'system behaviours',
  itemsWord: 'items fire',
  doesHeading: 'What people do',
  sysHeading: 'What the platform does',
  stateLabels: { env: 'Environment:', proj: 'Project:' },
  blocks: { admin: 'Admin', org: 'Org', client: 'Client', xc: 'Cross' },
  returnPill: 'return',
  lanesLabel: 'Actor lanes',
  lanesNote:
    'These six lanes are flow-time projections of the role model, not new roles: Client Lead ≈ the client manager, Client Team includes the client user, PX Lead is the manager / project-manager side, PX Admin is the admin. No permission follows from a lane.',
  gatePanel: {
    heading: 'The approval gate',
    body: `${flowStepByN('STEP 04').title} is the one hard gate in the flow: the admin reviews the briefing, blueprint and budget and — the part only this step covers — exactly what data the agent crew can reach. ${flowStepByN('STEP 04').sys[0] ?? ''}`,
  },
  edgePanel: {
    heading: 'The six loop edges',
    note: 'Both return edges (4 and 6) first fire at step 06 — that is the moment the loop closes and the platform starts measuring itself.',
  },
  scopePanel: {
    heading: 'Out of scope',
    tail: FLOW_SCOPE_TAIL.why,
    note: 'Same boundary as Run & evolve below: named here so the reader knows where the flow stops, and built nowhere.',
  },
  densityNote:
    'Minimalist: each step keeps its gate line, its outputs and its module activation on the page. The per-step action and system-behaviour lists are withheld behind the count on the right — a visible affordance, never a silent omission. Nothing that blocks a step is withheld: every gate line is written out in full.',
} as const

export const CROSSWALK = {
  intro:
    'The 32 screens in this prototype are cut by the delivery lifecycle — the ribbon on every project surface. Rev A defines the engagement lifecycle. Neither is renamed into the other: the map below is total on the delivery phases and functional, and it is what every gate strip in the product reads.',
  columns: ['Delivery phase (the screens)', 'Sub-label', 'Engagement phase (Rev A)', 'Gate'],
  otherDirection: 'The other direction',
  preProjectPill: 'pre-project',
} as const

export interface CrosswalkRow {
  deliveryKey: PhaseKey
  deliveryName: string
  subLabel: string
  engagementLabel: string
  gateName: string
}

export const crosswalkRows = (): readonly CrosswalkRow[] =>
  PHASES.map((phase) => {
    const engagement = engagementPhaseFor(phase.key)
    return {
      deliveryKey: phase.key,
      deliveryName: phase.name,
      subLabel: phase.subLabel,
      engagementLabel: `${engagement.number} · ${engagement.name}`,
      gateName: engagement.gateName,
    }
  })

export interface ReverseCrosswalkRow {
  phase: EngagementPhase
  deliveryNames: readonly string[]
}

export const reverseCrosswalkRows = (): readonly ReverseCrosswalkRow[] =>
  ENGAGEMENT_PHASES.map((phase) => ({
    phase,
    deliveryNames: phase.deliveryPhases.map(
      (key) => PHASES.find((p) => p.key === key)?.name ?? key
    ),
  }))

export const INVENTORY = {
  intro:
    'Rev A’s sheet 4, transcribed at full resolution. Every live module carries its one-line description and whether it came from the original sketch or was added by the review.',
  totalsAriaLabel: 'Rev A totals',
  kpis: [
    { label: 'Modules', figure: REV_A.totals.modules, qualifier: 'Rev A sheet-4 footer' },
    { label: 'Kept from sketch', figure: REV_A.totals.kept, qualifier: 'footer figure' },
    { label: 'Added by the review', figure: REV_A.totals.added, qualifier: 'footer figure' },
    { label: 'Removed / reclassified', figure: REV_A.totals.removed, qualifier: 'listed in full below' },
  ],
  reconciliationLead: 'Reconciliation —',
  reconciliationNote: REV_A_RECONCILIATION.note,
  reconciliationCounts: `Enumerated here: ${REV_A_RECONCILIATION.transcribed.modules} live (${REV_A_RECONCILIATION.transcribed.sketch} from sketch + ${REV_A_RECONCILIATION.transcribed.added} added) and ${REV_A_RECONCILIATION.transcribed.removed} removed.`,
  removedHeading: 'Removed / reclassified · not live modules',
  fromSketch: 'from sketch',
  added: 'added',
} as const

export interface LevelCard {
  level: ModuleLevel
  label: string
  scope: string
  modules: readonly ModuleEntry[]
  sketch: number
  addedCount: number
}

export const levelCards = (): readonly LevelCard[] =>
  MODULE_LEVELS.map((level) => {
    const modules = modulesAtLevel(level)
    return {
      level,
      label: LEVEL_LABELS[level],
      scope: LEVEL_SCOPES[level],
      modules,
      sketch: modules.filter((m) => m.origin === 'sketch').length,
      addedCount: modules.filter((m) => m.origin === 'added').length,
    }
  })

export const RUN_AND_EVOLVE_LABELS = {
  pill: 'named, not built',
} as const

export const ARCHITECTURE_DENSITY_NOTE = `Minimalist: this surface is a reference, so it carries every module name, every gate and every exit criterion in full — those are the facts a reviewer holding Rev A checks against. What it withholds is per-step procedure (the action and system-behaviour lists on the customer flow), behind a visible count. Two boundaries are stated rather than hidden: the sheet-4 total does not reconcile and the note says so, and Run & evolve is named and not built.`

export const INVENTORY_TOTALS = {
  live: MODULE_REGISTRY.length,
  removed: REMOVED_MODULES.length,
  steps: FLOW_STEPS.length,
} as const
